import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TabMenuItem } from '../../core/models/menu/tab-menu-item';
import { MenuService } from '../../core/services/menu.service';
import { environment } from '../../../environments/environment';
import { UserProfile } from '../../core/models/user/user-profile';
import { AccountService } from '../../core/services/api/account.service';
import { DefaultProfileImage } from '../../core/services/constant';
import { PortfolioService } from '../../core/services/api/portfolio.service';
import { Subject, asapScheduler, filter, finalize, observeOn, takeUntil } from 'rxjs';
import { PortfolioSummary } from '../../core/models/portfolio/portfolio-summary';
import { ErrorService } from '../../core/services/error.service';
import { Role } from '../../core/enum/role.enum';
import { StorageService } from '../../core/services/storage.service';
import { InvestorType } from '../../core/enum/investor-type.enum';
import { TCFCampaignService } from '../../core/services/api/tcf-campaign.service';
import { PstxAccountService } from '../../core/services/pstx-account.service';
import { Wallet } from '../../core/models/mysec/wallet';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DashboardTodoService } from '../../core/services/dashboard-todo.service';
import { PopupService } from '../../shared/services/popup/popup.service';
import { BlockUiService } from '../../shared/services/blockUi/block-ui.service';
import { GtmDataLayerService } from '../../core/services/gtm-data-layer.service';

@Component({
  selector: 'app-investor-dashboard',
  templateUrl: './investor-dashboard.component.html',
  standalone: false,
})
export class InvestorDashboardComponent implements OnInit, OnDestroy {
  public environment = environment;
  ieoBaseUrl: string = environment.ieoBaseUrl;
  tabs: TabMenuItem[] = [];
  /** FRC-749: the bottom nav's Profile tab (mobile/tablet only). */
  profileMenu: TabMenuItem;
  /** FRC-749: Things To Do count for the Dashboard nav item; null until known. */
  todoCount: number | null = null;
  profile: UserProfile;
  selectedMenu: string;
  collapsedGroups = new Set<string>();

  tokenBalance: number = 0;
  isTokenBalanceLoading: boolean = false;

  portfolioSummary: PortfolioSummary;
  isPortfolioSummaryCollapsed: boolean = true;
  isPortfolioSummaryLoading: boolean = true;
  role: Role;

  pstxWallet: Wallet;
  isPstxWalletFound: boolean = false;

  defaultProfileImage = DefaultProfileImage;

  Role: typeof Role = Role;
  InvestorType: typeof InvestorType = InvestorType;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private menuService: MenuService,
    private accountService: AccountService,
    private portfolioService: PortfolioService,
    private errorService: ErrorService,
    private storageService: StorageService,
    private tcfCampaignService: TCFCampaignService,
    private pstxAccountService: PstxAccountService,
    private router: Router,
    private route: ActivatedRoute,
    private dashboardTodoService: DashboardTodoService,
    private popupService: PopupService,
    private blockUiService: BlockUiService,
    private gtmDataLayerService: GtmDataLayerService,
  ) {}

  private readonly destroy$ = new Subject<void>();

  ngOnInit() {
    this.tabs = this.menuService.menu.investorDashboardMenu;
    this.profileMenu = this.menuService.menu.investorDashboardProfileMenu;

    // FRC-749: the dashboard page publishes its count from its own ngDoCheck, i.e. after this
    // (parent) view was checked — hop to the next microtask so the badge update starts a fresh
    // change-detection pass instead of tripping ExpressionChangedAfterItHasBeenChecked.
    // Reset first: the service is root-scoped and the navbar's logout does not clear it, so it
    // could otherwise replay the previous session's count (and cancels any stale refresh).
    this.dashboardTodoService.reset();
    this.dashboardTodoService.count$
      .pipe(observeOn(asapScheduler), takeUntil(this.destroy$))
      .subscribe((count) => (this.todoCount = count));

    this.role = this.storageService.role;

    this.accountService.currentUser.subscribe((profile) => {
      this.profile = profile;
    });

    this.pstxAccountService.getWallet().subscribe((wallet: Wallet) => {
      this.pstxWallet = wallet;
    });

    this.pstxAccountService.getIsWalletFound().subscribe((isFound: boolean) => {
      this.isPstxWalletFound = isFound;
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.getActiveMenu();
        this.refreshTodoCount();
      });

    this.getActiveMenu();
    this.getPortfolioSummary();
    this.refreshTodoCount();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * FRC-749: populate the Dashboard badge on every page in this layout. Skipped on /dashboard
   * itself — that page loads the same data and publishes the authoritative count. Re-checked on
   * every navigation: fills the badge if /dashboard never published, and re-fetches it once the
   * count is stale (DashboardTodoService.STALE_AFTER_MS) or came from a partly failed fetch, so
   * an action taken off /dashboard (e.g. cancelling an order) shows on a later navigation.
   */
  private refreshTodoCount() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.router.url.split(/[?#]/)[0] === '/dashboard') return;
    this.dashboardTodoService.refreshIfStale();
  }

  /** Dashboard nav item shows the Things To Do count (hidden for null / 0). */
  badgeFor(item: TabMenuItem): number | string | null {
    if (item.url === '/dashboard') {
      return this.todoCount || null;
    }
    return item.badge || null;
  }

  /**
   * Profile sheet "Log Out" (bottom nav). Same flow as the navbar's logout()
   * (navbar.component.ts) — confirm, call the API, go home, clear GTM user + wallet state.
   */
  logout() {
    this.popupService.confirm('common.confirmLogout').then(
      () => {
        this.blockUiService.open();

        this.accountService
          .logout()
          .subscribe(() => {
            this.router.navigate(['/']);
            this.gtmDataLayerService.logUser();
            this.pstxAccountService.resetState();
            this.dashboardTodoService.reset();
          })
          .add(() => {
            this.blockUiService.close();
          });
      },
      () => {},
    );
  }

  getActiveMenu() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const path = window?.location?.pathname;

    for (let item of this.tabs) {
      if (!item?.submenus?.length && item.url === path) {
        this.selectedMenu = item.name;
        return;
      }
    }

    for (let item of this.tabs) {
      const submenu = item?.submenus?.find(
        (x) => !x.isExternal && !!x.url && path.startsWith(x.url),
      );
      if (submenu) {
        this.selectedMenu = submenu.name;
        return;
      }
    }

    this.selectedMenu = undefined;
  }

  isGroupExpanded(group: TabMenuItem): boolean {
    return !this.collapsedGroups.has(group.name);
  }

  toggleGroup(group: TabMenuItem) {
    if (this.collapsedGroups.has(group.name)) {
      this.collapsedGroups.delete(group.name);
    } else {
      this.collapsedGroups.add(group.name);
    }
  }

  togglePortfolioSummary() {
    this.isPortfolioSummaryCollapsed = !this.isPortfolioSummaryCollapsed;
  }

  getPortfolioSummary() {
    this.portfolioService
      .getPortfolio(1, 1)
      .pipe(
        finalize(() => {
          this.isPortfolioSummaryLoading = false;
        }),
      )
      .subscribe({
        next: (data) => {
          this.portfolioSummary = data.summary;
        },
        error: (err) => {
          this.errorService.showError(err);
        },
      });
  }

  getTokenBalance() {
    this.tcfCampaignService
      .getTotalInvestments()
      .pipe(
        finalize(() => {
          this.isTokenBalanceLoading = false;
        }),
      )
      .subscribe({
        next: (data) => {
          this.tokenBalance = data;
        },
      });
  }
}
